import { useQuery, useInfiniteQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { useMemo, useRef } from 'react'
import type { Transport } from '@connectrpc/connect'

import { listInvoices, getInvoice, getInvoiceDetail, createInvoice, updateInvoice, deleteInvoice, getInvoiceLitesByIds, generateInvoicePortalLink, generateInvoicePdf, generatePaymentReceiptPdf, getBooking, sendInvoice, getInvoiceStats } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListInvoicesRequestSchema, GetInvoiceRequestSchema, GetInvoiceResponseSchema, GetInvoiceDetailRequestSchema, GetInvoiceLitesByIdsRequestSchema, CreateInvoiceRequestSchema, GetInvoiceStatsRequestSchema, GeneratePaymentReceiptPdfRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { type InvoiceStatusEnum, type ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { type Invoice, type InvoiceLineItem, QuoteLineItemInputSchema, SendInvoiceRequestSchema } from '@/gen/cortado/v1/invoices_pb'
import { organizationStore, useCurrentOrganization } from './organization.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { connectTransport } from '@/lib/connect-transport'
import { invalidateCrossEntityEmailCaches } from '@/stores/email-thread-cache'

export interface ListInvoicesQueryOptions {
  organizationId: string
  bookingId?: string
  contactId?: string
  statuses?: InvoiceStatusEnum[]
  since?: Date
  titleFilter?: string
  limit?: number
  offset?: number
}

export function createListInvoicesRequest(options: ListInvoicesQueryOptions) {
  return create(ListInvoicesRequestSchema, {
    organizationId: options.organizationId,
    bookingId: options.bookingId,
    contactId: options.contactId,
    statuses: options.statuses || [],
    since: options.since ? dateToTimestamp(options.since) : undefined,
    titleFilter: options.titleFilter,
    limit: options.limit,
    offset: options.offset,
  })
}

export function getInvoiceQueryOptions(invoiceId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getInvoice, create(GetInvoiceRequestSchema, { id: invoiceId }), { transport })
}

/**
 * Query options for the GetInvoiceDetail aggregate RPC, which returns the
 * invoice plus its contact and (when present) parent booking in a single
 * round-trip. Used by the invoice detail route loader to warm the per-entity
 * caches and collapse the invoice -> contact / booking request waterfall.
 */
export function getInvoiceDetailQueryOptions(invoiceId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getInvoiceDetail, create(GetInvoiceDetailRequestSchema, { id: invoiceId }), { transport })
}

/**
 * Compares two arrays of invoice line items to detect changes
 * Returns true if items differ in count, order, content, or properties
 *
 * This helper is useful for determining if line items have changed before
 * updating an invoice, as line item changes trigger new revisions in the backend.
 *
 * @param original - The original line items from the invoice
 * @param updated - The updated line items to compare
 * @returns true if line items have changed, false otherwise
 */
export function hasInvoiceLineItemsChanged(
  original: InvoiceLineItem[] | undefined,
  updated: InvoiceLineItem[]
): boolean {
  if (!original) return updated.length > 0
  if (original.length !== updated.length) return true

  const normalizeString = (value: string | undefined) => value || ''
  const normalizeNumber = (value: number | undefined) => value ?? 0
  const normalizeBool = (value: boolean | undefined) => value ?? true

  return original.some((origItem, index) => {
    const updatedItem = updated[index]
    if (!updatedItem) return true

    return (
      origItem.sourceItemId !== updatedItem.sourceItemId ||
      normalizeString(origItem.name) !== normalizeString(updatedItem.name) ||
      origItem.quantity !== updatedItem.quantity ||
      Number(origItem.unitPriceCents) !== Number(updatedItem.unitPriceCents) ||
      normalizeString(origItem.description) !== normalizeString(updatedItem.description) ||
      origItem.unit !== updatedItem.unit ||
      origItem.sortOrder !== updatedItem.sortOrder ||
      normalizeBool(origItem.isTaxable) !== normalizeBool(updatedItem.isTaxable) ||
      normalizeString(origItem.salesTaxRateId) !== normalizeString(updatedItem.salesTaxRateId) ||
      normalizeString(origItem.salesTaxRateName) !== normalizeString(updatedItem.salesTaxRateName) ||
      normalizeNumber(origItem.salesTaxRate) !== normalizeNumber(updatedItem.salesTaxRate)
      // salesTaxCents is intentionally not compared: the server recomputes it
      // from the discounted line share, so the editor's pre-discount estimate
      // legitimately differs from the stored value without any user change.
    )
  })
}

/**
 * Calculates total paid amount from payment schedule
 *
 * @param amountPaidCents - Total amount paid in cents (from invoice)
 * @returns Amount paid in cents
 */
export function calculateAmountPaid(amountPaidCents?: bigint): number {
  if (!amountPaidCents) return 0
  return Number(amountPaidCents)
}

/**
 * Calculates amount due (remaining balance)
 *
 * @param amountDueCents - Total amount due in cents (from invoice)
 * @returns Amount due in cents
 */
export function calculateAmountDue(amountDueCents?: bigint): number {
  if (!amountDueCents) return 0
  return Number(amountDueCents)
}

/**
 * Checks if invoice is fully paid
 *
 * @param amountDueCents - Amount due in cents
 * @returns true if invoice is fully paid
 */
export function isInvoiceFullyPaid(amountDueCents?: bigint): boolean {
  return amountDueCents === BigInt(0)
}

/**
 * Hook to fetch invoices for an organization or booking
 *
 * This hook:
 * - Fetches invoices for the selected organization
 * - Uses lightweight InvoiceLite format optimized for list views
 * - Supports filtering by booking ID, contact ID, statuses, dates, and pagination
 * - Returns lightweight invoice data with pre-calculated totals
 *
 * @param options - Optional filters for booking ID, contact ID, statuses, dates, and pagination
 * @returns Query result with invoices array (InvoiceLite[]), total count, loading state, and error
 */
export function useInvoicesList(options?: {
  bookingId?: string
  contactId?: string
  statuses?: InvoiceStatusEnum[]
  since?: Date
  limit?: number
  offset?: number
  titleFilter?: string
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return createListInvoicesRequest({
      organizationId: selectedOrganizationId || '',
      bookingId: options?.bookingId,
      contactId: options?.contactId,
      statuses: options?.statuses || [],
      since: options?.since,
      titleFilter: options?.titleFilter,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.bookingId, options?.contactId, options?.statuses, options?.since, options?.titleFilter, options?.limit, options?.offset])

  const query = useQuery(listInvoices, request, {
    enabled: !!selectedOrganizationId && (options?.enabled ?? true),
  })

  return {
    invoices: query.data?.invoices || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Page size for the paginated invoices list. */
const INVOICES_PAGE_SIZE = 50

/**
 * Paginated variant of {@link useInvoicesList} for the Invoices page.
 *
 * Fetches invoices in offset-based pages of {@link INVOICES_PAGE_SIZE} and
 * exposes `fetchNextPage`/`hasMore` for a "Load more" affordance, replacing the
 * previous bulk `limit: 200` fetch. Paging is driven off the server-computed
 * `total_count` (a real COUNT over the same filter), matching the events/leads
 * pattern.
 */
export function useInfiniteInvoicesList(options?: {
  statuses?: InvoiceStatusEnum[]
  since?: Date
  titleFilter?: string
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(() => ({
    organizationId: selectedOrganizationId || '',
    statuses: options?.statuses || [],
    since: options?.since ? dateToTimestamp(options.since) : undefined,
    titleFilter: options?.titleFilter,
    limit: INVOICES_PAGE_SIZE,
    offset: 0,
  }), [selectedOrganizationId, options?.statuses, options?.since, options?.titleFilter])

  const query = useInfiniteQuery(listInvoices, input, {
    enabled: !!selectedOrganizationId,
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.invoices.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  const invoices = useMemo(
    () => query.data?.pages.flatMap((page) => page.invoices) ?? [],
    [query.data],
  )
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    invoices,
    totalCount,
    hasMore: invoices.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch aggregate invoice statistics for the current organization.
 *
 * Returns organization-wide totals/counts bucketed to match the invoices list
 * page stat cards. This replaces the previous approach of fetching every invoice
 * and computing the buckets client-side.
 *
 * Buckets (see GetInvoiceStatsResponse):
 *  - open      = Draft, Sent, Viewed, PaymentPending, PartiallyPaid, unknown
 *  - overdue   = Overdue
 *  - paid      = Paid, Overpaid
 *  - cancelled = Cancelled
 *
 * @returns Query result with per-bucket totals (cents) and counts, plus loading/error state
 */
export function useInvoiceStats() {
  const { currentOrganization } = useCurrentOrganization()

  const query = useQuery(
    getInvoiceStats,
    create(GetInvoiceStatsRequestSchema, {
      organizationId: currentOrganization?.id ?? '',
    }),
    { enabled: !!currentOrganization?.id }
  )

  return {
    stats: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    // hasLoaded flips once the query settles (success OR error) so the page's
    // skeleton gate releases even when the stats RPC fails — the stat cards
    // double as tab buttons and must render with placeholder values on error.
    hasLoaded: !query.isPending,
  }
}

/**
 * Hook to fetch invoices for a specific parent entity (booking, contact, etc.)
 *
 * This hook follows the same pattern as useQuotesForParent:
 * - Fetches invoices filtered by parent type and parent ID
 * - Uses lightweight InvoiceLite format optimized for list views
 * - Supports additional filtering by statuses, dates, and pagination
 * - Returns lightweight invoice data with pre-calculated totals
 *
 * @param options - Filters for parent type, parent ID, statuses, dates, and pagination
 * @returns Query result with invoices array (InvoiceLite[]), total count, loading state, and error
 */
export function useInvoicesForParent(options?: {
  parentType?: ParentTypeEnum
  parentId?: string
  statuses?: InvoiceStatusEnum[]
  since?: Date
  limit?: number
  offset?: number
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    // Map parentType to the appropriate filter field
    // Currently ListInvoices supports bookingId and contactId
    let bookingId: string | undefined
    let contactId: string | undefined

    if (options?.parentType && options?.parentId) {
      // Map parent type to appropriate field
      // In the future, this could be extended to other parent types
      if (options.parentType.toString().includes('BOOKING')) {
        bookingId = options.parentId
      } else if (options.parentType.toString().includes('CONTACT')) {
        contactId = options.parentId
      }
    }

    return create(ListInvoicesRequestSchema, {
      organizationId: selectedOrganizationId || '',
      bookingId,
      contactId,
      statuses: options?.statuses || [],
      since: options?.since ? dateToTimestamp(options.since) : undefined,
      limit: options?.limit,
      offset: options?.offset,
    })
  }, [selectedOrganizationId, options?.parentType, options?.parentId, options?.statuses, options?.since, options?.limit, options?.offset])

  const query = useQuery(listInvoices, request, {
    enabled: !!selectedOrganizationId && !!options?.parentId,
  })

  return {
    invoices: query.data?.invoices || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch a single invoice by ID
 *
 * This hook:
 * - Fetches a fully enriched invoice with all nested data
 * - Includes invoice items, payment schedules, users, and calculated totals
 * - Returns complete invoice data optimized for detail views
 *
 * @param invoiceId - The ID of the invoice to fetch
 * @returns Query result with invoice, loading state, and error
 */
export function useInvoice(invoiceId?: string) {
  const query = useQuery(
    getInvoice,
    create(GetInvoiceRequestSchema, {
      id: invoiceId ?? '',
    }),
    {
      enabled: !!invoiceId,
      refetchInterval: 30_000,
      select: (data) => data?.invoice,
    }
  )

  return {
    invoice: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to fetch InvoiceLite objects by an array of IDs
 *
 * This hook follows the normalized data pattern where the parent entity
 * (e.g., Booking) provides invoice IDs, and we fetch the invoice data separately.
 *
 * **Key Features:**
 * - Fetches lightweight InvoiceLite objects (optimized for list/card views)
 * - Only fetches when IDs array is non-empty
 * - Returns invoices filtered by RBAC (only invoices user has access to)
 * - Stable query key based on sorted IDs
 *
 * @param ids - Array of invoice IDs to fetch
 * @returns Query result with invoices array (InvoiceLite[]), loading state, and error
 *
 * @example
 * ```typescript
 * // Get invoice IDs from booking (source of truth)
 * const invoiceIds = booking?.invoiceIds || []
 *
 * // Fetch those specific invoices
 * const { invoices, isLoading, hasLoaded } = useInvoiceLitesByIds(invoiceIds)
 * ```
 */
export function useInvoiceLitesByIds(ids: string[]) {
  // Create stable key from sorted IDs to prevent unnecessary refetches
  const sortedIds = useMemo(() => [...ids].sort(), [ids])

  const query = useQuery(
    getInvoiceLitesByIds,
    create(GetInvoiceLitesByIdsRequestSchema, { ids: sortedIds }),
    {
      enabled: sortedIds.length > 0,
      select: (data) => data?.invoices || [],
    }
  )

  return {
    invoices: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to create a new invoice
 *
 * This hook provides mutation capabilities for creating new invoices.
 *
 * **Two Creation Modes:**
 *
 * 1. **Quote-Based Invoice** (from accepted quote):
 *    - Provide `quoteId` and `acceptedQuoteRevisionId`
 *    - Line items, payment schedules, and pricing inherit from quote
 *    - Can optionally override any inherited values
 *
 * 2. **Standalone Invoice** (created from scratch):
 *    - Omit `quoteId` and `acceptedQuoteRevisionId`
 *    - Must provide `title`, `issueDate`, and at least one line item
 *    - Payment schedules are optional
 *
 * **Required Fields (all modes):**
 * - `organizationId` - The organization creating the invoice
 * - `bookingId` - The booking this invoice is for
 * - `contactId` - The customer contact
 *
 * **Cache Management:**
 * - Automatically adds the new invoice to the cache
 * - Invalidates list queries to reflect the new invoice
 * - Optimistic updates ensure immediate UI feedback
 *
 * @returns Mutation result with createInvoice function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { createInvoice } = useCreateInvoice()
 *
 * // Create standalone invoice
 * await createInvoice(create(CreateInvoiceRequestSchema, {
 *   organizationId: org.id,
 *   bookingId: booking.id,
 *   contactId: contact.id,
 *   title: 'Event Services Invoice',
 *   issueDate: dateToTimestamp(new Date()),
 *   lineItems: [...],
 *   paymentSchedules: [...],
 *   discountId: 'discount-id',
 *   enforcedTipEnabled: true,
 *   enforcedTipPercentage: 20,
 * }))
 * ```
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createInvoice,
    {
      onSuccess: (response) => {
        const newInvoice = response.invoice

        if (!newInvoice) return

        // Add to individual invoice cache
        const queryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: newInvoice.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetInvoiceResponseSchema, { invoice: newInvoice })
        )

        // Invalidate list queries to reflect new invoice
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInvoices,
            transport,
            cardinality: undefined,
          }),
        })

        // Invalidate booking query to update its invoiceIds array
        // The booking stores invoice IDs as a denormalized field, so we must
        // invalidate it when an invoice is created to update that array
        if (newInvoice.bookingId) {
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({
              schema: getBooking,
              transport,
              input: { id: newInvoice.bookingId },
              cardinality: 'finite',
            }),
          })
        }
      },
    }
  )

  return {
    mutation,
    createInvoice: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an existing invoice
 *
 * This hook provides mutation capabilities for updating invoices with intelligent
 * revision handling based on which fields are modified.
 *
 * **Revision Behavior:**
 * The backend uses the field mask to determine if a new revision should be created:
 *
 * - **Metadata updates** (title, description) - Update in-place, no new revision
 * - **Financial changes** (sales_tax_rate, discount fields, tip fields) - Create new revision
 * - **Line item changes** (line_items) - Create new revision
 *
 * Use the `hasInvoiceLineItemsChanged()` helper to detect line item modifications before updating.
 *
 * **Field Mask:**
 * Always include a field mask in UpdateInvoiceRequest to specify which fields to update.
 * This ensures precise control over what changes and whether a revision is created.
 *
 * **Lock Status:**
 * Locked invoices cannot be updated. The backend will return an error if you attempt
 * to update a locked invoice.
 *
 * **Cache Management:**
 * - Automatically updates the individual invoice cache with the latest data
 * - Invalidates list queries to reflect changes across the application
 * - Optimistic updates ensure immediate UI feedback
 *
 * @returns Mutation result with updateInvoice function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { updateInvoice } = useUpdateInvoice()
 *
 * // Update metadata only (no new revision)
 * await updateInvoice(create(UpdateInvoiceRequestSchema, {
 *   id: invoice.id,
 *   title: 'Updated Title',
 *   updateMask: create(FieldMaskSchema, {
 *     paths: ['title']
 *   })
 * }))
 *
 * // Update line items (creates new revision)
 * await updateInvoice(create(UpdateInvoiceRequestSchema, {
 *   id: invoice.id,
 *   lineItems: newLineItems,
 *   updateMask: create(FieldMaskSchema, {
 *     paths: ['line_items']
 *   })
 * }))
 * ```
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listInvoicesKey = createConnectQueryKey({
    schema: listInvoices,
    transport,
    cardinality: undefined,
  })
  const invoiceLitesByIdsKey = createConnectQueryKey({
    schema: getInvoiceLitesByIds,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(updateInvoice, {
    onSuccess: (response) => {
      const updatedInvoice = response.invoice
      if (!updatedInvoice) return

      // Update individual invoice cache with properly typed protobuf message
      const queryKey = createConnectQueryKey({
        schema: getInvoice,
        transport,
        input: { id: updatedInvoice.id },
        cardinality: 'finite',
      })
      queryClient.setQueryData(
        queryKey,
        create(GetInvoiceResponseSchema, { invoice: updatedInvoice })
      )

      queryClient.setQueriesData({ queryKey: listInvoicesKey }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const data = old as { invoices?: Array<Record<string, unknown>> }
        if (!data.invoices) return old
        return {
          ...data,
          invoices: data.invoices.map((invoice) =>
            invoice['id'] === updatedInvoice.id ? { ...invoice, status: updatedInvoice.status } : invoice
          ),
        }
      })

      queryClient.setQueriesData({ queryKey: invoiceLitesByIdsKey }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const data = old as { invoices?: Array<Record<string, unknown>> }
        if (!data.invoices) return old
        return {
          ...data,
          invoices: data.invoices.map((invoice) =>
            invoice['id'] === updatedInvoice.id ? { ...invoice, status: updatedInvoice.status } : invoice
          ),
        }
      })

      // Invalidate list caches so they refetch with correct InvoiceLite types.
      // The direct patches above only keep status displays responsive.
      queryClient.invalidateQueries({ queryKey: listInvoicesKey })
      queryClient.invalidateQueries({ queryKey: invoiceLitesByIdsKey })
    },
  })

  return {
    mutation,
    updateInvoice: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to delete an invoice
 *
 * This hook:
 * - Provides mutation function to soft delete invoices
 * - Automatically invalidates invoice list and detail queries on success
 * - Backend validates that invoice has no payment schedules before deleting
 * - Backend checks that invoice is not locked
 *
 * @returns Mutation result with deleteInvoice function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { deleteInvoice, isLoading } = useDeleteInvoice()
 *
 * await deleteInvoice(create(DeleteInvoiceRequestSchema, {
 *   id: invoice.id
 * }))
 * ```
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    deleteInvoice,
    {
      onSuccess: (_response, variables) => {
        const deletedInvoiceId = variables.id

        // Remove from individual invoice cache
        const queryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: deletedInvoiceId },
          cardinality: 'finite',
        })

        queryClient.removeQueries({ queryKey })

        // Invalidate list queries to reflect deletion
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInvoices,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  return {
    mutation,
    deleteInvoice: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      return mutation.mutateAsync(request)
    },
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to prefetch an invoice on hover
 *
 * This hook provides a prefetch function that can be called on hover
 * to load invoice data into the cache before the user clicks.
 * Skips prefetching if the data is already in the cache.
 *
 * @returns Prefetch function
 */
export function usePrefetchInvoice() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchInvoice = async (invoiceId: string) => {
    const queryKey = createConnectQueryKey({
      schema: getInvoice,
      transport,
      input: { id: invoiceId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      getInvoiceQueryOptions(invoiceId, transport)
    )
  }

  return { prefetchInvoice }
}

/**
 * Hook to generate a shareable portal link for an invoice
 *
 * This hook:
 * - Generates a JWT token for customer portal access
 * - Returns a shareable URL that allows viewing and paying the invoice
 * - Supports both inquiry-scoped tokens (if booking has inquiry) and booking-scoped tokens
 *
 * @returns Mutation result with generateLink function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { generateLink, isLoading } = useGenerateInvoicePortalLink()
 *
 * const handleShare = async () => {
 *   const response = await generateLink(
 *     create(GenerateInvoicePortalLinkRequestSchema, { invoiceId: invoice.id })
 *   )
 *   // Use copyToClipboard, not navigator.clipboard.writeText — Safari blocks
 *   // writeText after an async gap (transient user activation is consumed).
 *   if (!copyToClipboard(response.url)) throw new Error('Copy failed')
 *   toast.success('Client portal link copied')
 * }
 * ```
 */
export function useGenerateInvoicePortalLink() {
  const mutation = useMutation(generateInvoicePortalLink)

  return {
    generateLink: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to generate (or retrieve the cached) PDF for an invoice.
 *
 * @returns Mutation result with generatePdf function, loading state, and error handling
 *
 * @example
 * ```typescript
 * const { generatePdf, isLoading } = useGenerateInvoicePdf()
 *
 * const response = await generatePdf(
 *   create(GenerateInvoicePdfRequestSchema, { invoiceId: invoice.id })
 * )
 * // response.downloadUrl / response.filename feed downloadFile()
 * ```
 */
export function useGenerateInvoicePdf() {
  const mutation = useMutation(generateInvoicePdf)

  return {
    generatePdf: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/** Generate or retrieve the cached receipt PDF for a completed payment schedule. */
export function useGeneratePaymentReceiptPdf() {
  const mutation = useMutation(generatePaymentReceiptPdf)

  return {
    generateReceiptPdf: (invoiceId: string, paymentScheduleId: string) => mutation.mutateAsync(
      create(GeneratePaymentReceiptPdfRequestSchema, { invoiceId, paymentScheduleId })
    ),
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to duplicate an existing invoice as a new DRAFT
 *
 * Creates a new invoice by copying line items, discount, tip settings,
 * and payment method configuration from the source invoice.
 * The new invoice gets a fresh issue date and " (Copy)" appended to the title.
 * Payment schedules are NOT copied — the duplicate starts as a clean draft.
 */
export function useDuplicateInvoice() {
  const { createInvoice, isLoading, isError, error } = useCreateInvoice()

  const duplicateInvoice = async (invoice: Invoice) => {
    const lineItems = invoice.lineItems.map((item) =>
      create(QuoteLineItemInputSchema, {
        itemId: item.sourceItemId,
        quantity: item.quantity,
        unitPriceCents: item.unitPriceCents,
        name: item.name,
        description: item.description,
        unit: item.unit,
        isTaxable: item.isTaxable,
        salesTaxRateId: item.salesTaxRateId,
        salesTaxRateName: item.salesTaxRateName,
        salesTaxRate: item.salesTaxRate,
        salesTaxCents: item.salesTaxCents,
      })
    )

    return createInvoice(
      create(CreateInvoiceRequestSchema, {
        organizationId: invoice.organizationId,
        bookingId: invoice.bookingId,
        contactId: invoice.contactId,
        title: `${invoice.title} (Copy)`,
        description: invoice.description,
        issueDate: dateToTimestamp(new Date()),
        lineItems,
        discountId: invoice.discountId,
        discountName: invoice.discountName,
        discountType: invoice.discountType,
        discountValue: invoice.discountValue,
        enforcedTipEnabled: invoice.enforcedTipEnabled,
        enforcedTipPercentage: invoice.enforcedTipPercentage,
        tipAllocationMode: invoice.tipAllocationMode,
        checkoutPaymentMethods: [...invoice.checkoutPaymentMethods],
      })
    )
  }

  return {
    duplicateInvoice,
    isLoading,
    isError,
    error,
  }
}

/**
 * Hook to send an invoice email to the customer
 *
 * This hook:
 * - Sends an invoice email via Gmail with an authenticated portal link
 * - For DRAFT invoices: updates status to SENT and sets sentAt
 * - For already-sent invoices (SENT, OVERDUE, PARTIALLY_PAID): resends without changing status
 * - Rejects CANCELLED and PAID invoices
 * - Updates the invoice cache with the returned invoice
 * - Invalidates list queries to reflect status changes
 *
 * @returns Mutation result with sendInvoice function, loading state, and error handling
 */
export function useSendInvoice() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const pendingOperationKeys = useRef(new Map<string, string>())

  const mutation = useMutation(
    sendInvoice,
    {
      onSuccess: (response) => {
        const updatedInvoice = response.invoice

        if (!updatedInvoice) return

        // Update individual invoice cache
        const queryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: updatedInvoice.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetInvoiceResponseSchema, { invoice: updatedInvoice })
        )

        // Invalidate list queries to reflect status change
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listInvoices,
            transport,
            cardinality: undefined,
          }),
        })

        // Immediately patch InvoiceLite status in cache so cards update without waiting for refetch
        queryClient.setQueriesData(
          {
            queryKey: createConnectQueryKey({
              schema: getInvoiceLitesByIds,
              transport,
              cardinality: undefined,
            }),
          },
          (old: unknown) => {
            if (!old || typeof old !== 'object') return old
            const data = old as { invoices?: Array<Record<string, unknown>> }
            if (!data.invoices) return old
            return {
              ...data,
              invoices: data.invoices.map(inv =>
                inv['id'] === updatedInvoice.id ? { ...inv, status: updatedInvoice.status } : inv
              ),
            }
          }
        )

        // Invalidate getInvoiceLitesByIds caches for background sync
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInvoiceLitesByIds,
            transport,
            cardinality: undefined,
          }),
        })

        // Refresh the record's Conversations (and legacy Emails) tab — the sent
        // invoice email is now persisted
        invalidateCrossEntityEmailCaches(queryClient, transport)
      },
    }
  )

  return {
    sendInvoice: async (request: Parameters<typeof mutation.mutateAsync>[0]) => {
      const invoiceId = request.invoiceId
      if (!invoiceId) throw new Error('Invoice ID is required')

      const operationKey = request.idempotencyKey
        ?? pendingOperationKeys.current.get(invoiceId)
        ?? crypto.randomUUID()
      pendingOperationKeys.current.set(invoiceId, operationKey)

      const response = await mutation.mutateAsync(create(SendInvoiceRequestSchema, {
        invoiceId,
        idempotencyKey: operationKey,
      }))
      // A later user-initiated resend is a new logical operation. Retain the key
      // only across failed or ambiguous attempts of the current operation.
      pendingOperationKeys.current.delete(invoiceId)
      return response
    },
    isSending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
